import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthContext';
import { Button, Input, Label, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui';
import { toast } from 'sonner';
import SEO from '@/components/SEO';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { validatePassword, rateLimit } from '@/utils/validation';
import { HCAPTCHA_SITE_KEY, PASSWORD_REQUIREMENTS } from '@/utils/constants';
import { productionLogger } from '@/utils/production-logger';
import { Mail } from 'lucide-react';

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaReady, setCaptchaReady] = useState(false);
  const [captchaOptional, setCaptchaOptional] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const captchaRef = useRef<HCaptcha>(null);

  useEffect(() => {
    // Make CAPTCHA optional immediately for testing
    setCaptchaOptional(true);
    productionLogger.debug("CAPTCHA set to optional for testing");
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Rate limiting check
    const rateLimitKey = `auth_${email}_${Date.now()}`;
    if (!rateLimit(rateLimitKey, 5, 15 * 60 * 1000)) { // 5 attempts per 15 minutes
      toast.error("Too many attempts", {
        description: "Please wait before trying again.",
      });
      return;
    }
    
    if (!captchaToken && !captchaOptional) {
      toast.error("CAPTCHA Required", {
        description: "Please complete the CAPTCHA verification.",
      });
      return;
    }

    // Validate password for signup
    if (!isLogin && !validatePassword(password)) {
      toast.error("Invalid Password", {
        description: PASSWORD_REQUIREMENTS,
      });
      return;
    }

    setLoading(true);

    try {
      if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
          options: {
            captchaToken: captchaToken || undefined,
          },
        });
        if (error) throw error;
        toast.success("Welcome back!", {
          description: "You've been signed in successfully.",
        });
        // Redirect to account page after successful login
        navigate('/account');
      } else {
        // Use the current domain for redirect
        const redirectUrl = window.location.origin;
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: redirectUrl,
            captchaToken: captchaToken || undefined,
          }
        });
        if (error) throw error;
        toast.success("Account created!", {
          description: "Please check your email to verify your account.",
        });
      }
    } catch (error: any) {
      toast.error("Error", {
        description: error.message,
      });
      // Reset CAPTCHA on error
      setCaptchaToken(null);
      captchaRef.current?.resetCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast.error("Email Required", {
        description: "Please enter your email address.",
      });
      return;
    }

    setLoading(true);
    
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth?reset=true`,
      });
      
      if (error) throw error;
      
      toast.success("Reset Email Sent", {
        description: "Check your email for password reset instructions.",
      });
      
      setIsForgotPassword(false);
      setIsLogin(true);
    } catch (error: any) {
      toast.error("Error", {
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/account`,
        }
      });
      
      if (error) throw error;
    } catch (error: any) {
      toast.error("Google Sign In Error", {
        description: error.message,
      });
      setLoading(false);
    }
  };

  const onCaptchaChange = (token: string | null) => {
    setCaptchaToken(token);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sage to-terracotta p-4">
      <SEO 
        title={isLogin ? "Sign In - Boho Style Co." : "Sign Up - Boho Style Co."}
        description={isLogin ? "Sign in to your Boho Style Co. account to access favorites, orders, and exclusive bohemian fashion deals." : "Create your Boho Style Co. account to save favorites, track orders, and shop our exclusive bohemian fashion collection."}
        url={window.location.href}
      />
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-playfair text-primary">
            Boho Style Co.
          </CardTitle>
          <CardDescription>
            {isForgotPassword ? 'Reset your password' : (isLogin ? 'Welcome back' : 'Create your account')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {user ? (
            <div className="space-y-4 text-center">
              <p className="text-sm">You're already signed in.</p>
              <div className="flex gap-2 justify-center">
                <Button onClick={() => navigate('/')}>Go to Home</Button>
                <Button variant="outline" onClick={async () => {
                  await supabase.auth.signOut();
                  toast("Signed out");
                }}>
                  Sign out
                </Button>
              </div>
            </div>
          ) : isForgotPassword ? (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reset-email">Email</Label>
                <Input
                  id="reset-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="your@email.com"
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send Reset Email'}
              </Button>
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setIsForgotPassword(false)}
                  className="text-primary hover:underline text-sm"
                >
                  Back to sign in
                </button>
              </div>
            </form>
          ) : (
            <>
              <div className="space-y-4">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                >
                  <Mail className="mr-2 h-4 w-4" />
                  Continue with Google
                </Button>
                
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">
                      Or continue with email
                    </span>
                  </div>
                </div>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="your@email.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    minLength={8}
                  />
                  {!isLogin && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Password must be at least 8 characters with uppercase, lowercase, number, and special character
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-center gap-2">
                  <HCaptcha
                    ref={captchaRef}
                    sitekey={HCAPTCHA_SITE_KEY}
                    onVerify={onCaptchaChange}
                    onExpire={() => setCaptchaToken(null)}
                    onError={() => { setCaptchaToken(null); setCaptchaOptional(true); }}
                    onLoad={() => setCaptchaReady(true)}
                  />
                  {captchaOptional && (
                    <p className="text-xs text-muted-foreground">
                      CAPTCHA couldn’t load. You can try to continue without it.
                    </p>
                  )}
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                >
                  {loading ? 'Loading...' : (isLogin ? 'Sign In' : 'Sign Up')}
                </Button>
              </form>
              <div className="mt-4 space-y-2 text-center">
                {isLogin && (
                  <button
                    type="button"
                    onClick={() => setIsForgotPassword(true)}
                    className="text-primary hover:underline text-sm block w-full"
                  >
                    Forgot your password?
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setIsLogin(!isLogin)}
                  className="text-primary hover:underline text-sm"
                >
                  {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
                </button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Auth;